import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import moment from 'moment-timezone';
import { Route, generatePath, useHistory, useRouteMatch } from 'react-router-dom';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import { useFullStoryPageView } from 'hooks/useFullstoryPageView';
import useUserHook from 'hooks/useUserHook';

import { useMutationDeleteStory } from 'api/mutations/deleteStory.mutation';
import { useQueryGetDataStories } from 'api/queries/getDataStories.query';
import { useQueryGetProgramGeneralHierarchyList } from 'api/queries/getProgramGeneralHierarchyList.query';

import useIntersectionObserver from 'components/common/CustomHook/IsElementOnScreenHook/useIntersectionObserverHook';
import { FilterNames, getFiltersData } from 'components/common/GlobalFilterComponent/GlobalFilterComponent.utils';
import NoData from 'components/common/NoData/noData';
import { BottomHeaderSection } from 'components/common/PageContainer/BottomHeaderSection';
import { HeaderWrapper } from 'components/common/PageContainer/HeaderWrapper';
import { MainContent } from 'components/common/PageContainer/MainContent';
import PageContainer from 'components/common/PageContainer/PageContainer';
import { ProgramTopHeader } from 'components/common/ProgramTopHeader/ProgramTopHeader';

import DataStoriesHeader from 'components/Dashboards/Project/Components/DataStories/DataStoriesHeader/dataStoriesHeader';
import RenderExpandStoryModal from 'components/Dashboards/Project/Components/DataStories/RenderExpandStoryModal/renderExpandStoryModal';
import { IDataStoriesProps } from 'components/Dashboards/Project/Components/DataStories/dataStories.types';
import { executionConfig } from 'components/Dashboards/Project/ProjectWrapper/executionConfig';
import { CustomizedConfirmDeleteModal } from 'components/common';
import { AddStoryModal } from 'components/pages/DataStoriesPage/components/AddStoryModal/AddStoryModal';
import { SingleStory } from 'components/widgets/SingleStory/singleStory';

const DataStoriesPage = memo(({ showTreeTopHeader = true, project, contract }: IDataStoriesProps) => {
    const { path } = useRouteMatch();
    const { handleSnackBar } = useCustomSnackBar();
    const [deleteModalShow, setDeleteModalShow] = useState<boolean>(false);
    const [removeFeed, setRemoveFeed] = useState<number | null>(null);
    const [showAddStory, setShowAddStory] = useState<any>(false);
    const [editStory, setEditStory] = useState<any>(null);
    const [chosenDate, setChosenDate] = useState<string | null>(null);
    const [textSearch, setTextSearch] = useState<string | null>(null);
    const { ability, username } = useUserHook();
    const ref = useRef<any>();
    const entry = useIntersectionObserver(ref, {});
    const isVisible = !!entry?.isIntersecting;
    const { data: projectHierarchyList } = useQueryGetProgramGeneralHierarchyList('TreeProgramFilter');
    const history = useHistory();
    const params = useMemo(() => {
        return {
            filter: {
                filters: getFiltersData({
                    [FilterNames.CONTRACT_DATA_STORY]: [!!contract ? contract.id : -1],
                    [FilterNames.PROJECT_DATA_STORY]: [!!project ? project.id : -1],
                    [FilterNames.CREATED_MONTH_DATA_STORY]: chosenDate ? [chosenDate] : [],
                    [FilterNames.TEXT_AND_TAGS_DATA_STORY]: textSearch ? [textSearch] : [],
                }),
            },
        };
    }, [project, contract, chosenDate, textSearch]);

    const {
        data: dataStories,
        fetchNextPage,
        hasNextPage,
        refetch,
    } = useQueryGetDataStories({
        filter: params,
        pageParam: { page: 0, size: 4 },
        projectId: project?.id || -1,
    });

    const { mutate: mutateDeleteStory, isLoading: isLoadingDeleteStory } = useMutationDeleteStory();

    useEffect(() => {
        if (editStory !== null) {
            setShowAddStory(true);
        }
    }, [editStory]);

    useEffect(() => {
        if (projectHierarchyList) {
            const newLocation = generatePath(path, { projectId: project?.id, contractId: contract?.id });
            history.push(newLocation);
        }
    }, [projectHierarchyList, project, contract]);

    useEffect(() => {
        if (project !== null && isVisible && hasNextPage) {
            getMoreData();
        }
    }, [isVisible, hasNextPage]);

    const getMoreData = () => {
        fetchNextPage().then(() => null);
    };

    const addStory = () => {
        setShowAddStory(true);
    };

    const handleEditStory = useCallback((data) => {
        setEditStory({ ...data, file: data.imageName });
    }, []);

    const handleDeleteFeed = (id: number) => {
        mutateDeleteStory(id, {
            onSuccess: () => {
                refetch().then(() => {
                    handleSnackBar('Feed successfully deleted', 'success');
                    setDeleteModalShow(false);
                });
            },
            onError: (error) => {
                DEFAULT_ERROR({ error, handleError: handleSnackBar });
            },
        });
    };

    const confirmDelete = useCallback((id: number) => {
        setRemoveFeed(id);
        setDeleteModalShow(true);
    }, []);

    const handleFetchDatePicker = (date) => {
        const chosenDate = moment(new Date(date)).format('YYYYMM');
        setChosenDate(chosenDate);
    };

    const handleClearDatePicker = () => {
        setChosenDate(null);
    };

    const onTextSearch = (value) => {
        if (value.trim().length > 0) {
            setTextSearch(value);
        }
    };

    const onClearSearch = () => {
        setTextSearch(null);
    };

    const oneYearAgo = moment().subtract(1, 'year').valueOf();
    const fullPath = generatePath(path, { projectId: project?.id, contractId: contract?.id });

    useFullStoryPageView({ pageName: executionConfig.dataStories.title });

    return (
        <PageContainer>
            <HeaderWrapper>
                <DataStoriesHeader
                    pageTitle={executionConfig.dataStories.title}
                    addStory={addStory}
                    handleDateData={handleFetchDatePicker}
                    handleClearDate={handleClearDatePicker}
                    onTextSearch={onTextSearch}
                    onClearSearch={onClearSearch}
                    isReadOnly={ability.can('view', 'DataStoryRead')}
                    showTreeTopHeader={showTreeTopHeader}
                    startDate={dataStories?.pages[0]?.startDate || oneYearAgo}
                    endDate={dataStories?.pages[0]?.endDate || oneYearAgo}
                />
                <BottomHeaderSection>
                    <ProgramTopHeader pageTitle={executionConfig.dataStories.title} />
                </BottomHeaderSection>
            </HeaderWrapper>
            <MainContent>
                {dataStories && dataStories.pages[0]?.dataStory.length > 0 ? (
                    <>
                        {dataStories.pages.map((page) => {
                            return page.dataStory.map((item) => (
                                <SingleStory
                                    key={item.id}
                                    storyId={item.id}
                                    edit={handleEditStory}
                                    username={username}
                                    deleteStory={confirmDelete}
                                    isAdmin={ability.can('all', 'Admin')}
                                    token={dataStories.pages[0]?.token}
                                    projectId={project?.id}
                                    contractId={contract?.id}
                                    path={path}
                                />
                            ));
                        })}
                        <div style={{ width: '100%', height: '10px' }} ref={ref} />
                    </>
                ) : (
                    <NoData />
                )}
            </MainContent>
            <Route exact path={`${fullPath}/:storyId`} render={() => <RenderExpandStoryModal />} />
            <AddStoryModal
                open={showAddStory}
                onClose={() => {
                    setShowAddStory(false);
                    setEditStory(null);
                }}
                project={project}
                contract={contract}
                dataStories={dataStories}
                editStory={editStory}
            />

            {removeFeed && (
                <CustomizedConfirmDeleteModal
                    isShowModal={deleteModalShow}
                    hideModal={() => {
                        setDeleteModalShow(false);
                    }}
                    text={'Are you sure you want to delete this Feed?'}
                    onConfirm={() => handleDeleteFeed(removeFeed)}
                    isLoading={isLoadingDeleteStory}
                />
            )}
        </PageContainer>
    );
});
export default DataStoriesPage;
